/**
 * LinkPage — offentlig, snap-vennlig lenke-hub (allmylinks-alternativ).
 *
 * 3-siders flyt for å unngå link-forhåndsvisning-blokkering:
 *   /link/:username          → landing (avatar + navn + knapp)
 *   /link/:username/gate     → 18+-bekreftelse (setter cookie)
 *   /link/:username/links    → link-listen (JS-fetch, krever cookie)
 *
 * Ingen Layout/nav. noindex/nofollow. Nøytral OG-meta.
 * Fetching direkte via axios uten auth (helt anonymt).
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// På link-side-domenet (allelenker.no) sender vi API-kall same-origin gjennom
// Cloudflare Worker som proxier. Dette unngår cross-site cookie-blokkering
// i Chrome/Safari (tredjeparts-cookies).
const LINK_API = typeof window !== "undefined" && window.location.hostname !== "" &&
                 !window.location.hostname.includes("norskerotikk")
  ? `${window.location.origin}/api`
  : API;
const publicApi = axios.create({ baseURL: LINK_API, withCredentials: true });

function useNeutralMeta(displayName) {
  useEffect(() => {
    const prev = document.title;
    document.title = displayName ? `${displayName}` : "Personlig side";

    const noindex = document.createElement("meta");
    noindex.name = "robots";
    noindex.content = "noindex, nofollow";
    document.head.appendChild(noindex);

    const desc = document.createElement("meta");
    desc.name = "description";
    desc.content = "Personlig lenkeside.";
    document.head.appendChild(desc);

    return () => {
      document.title = prev;
      try { document.head.removeChild(noindex); } catch { /* ignore */ }
      try { document.head.removeChild(desc); } catch { /* ignore */ }
    };
  }, [displayName]);
}

function ImgAvatar({ src, name }) {
  const initial = (name || "?").slice(0, 1).toUpperCase();
  if (!src) {
    return (
      <div
        className="w-28 h-28 rounded-full bg-white/10 flex items-center justify-center font-fraunces italic text-4xl text-[var(--cream)]"
        data-testid="link-avatar-placeholder"
      >
        {initial}
      </div>
    );
  }
  const full = src.startsWith("http") ? src : `${API}/media?path=${encodeURIComponent(src)}`;
  return (
    <img
      src={full}
      alt=""
      className="w-28 h-28 rounded-full object-cover"
      data-testid="link-avatar"
    />
  );
}

// ---------- SIDE 1: Landing (nøytral for bot) ----------

export function LinkLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await publicApi.get(`/link/${slug}/summary`);
        if (alive) setState({ loading: false, data: r.data, error: "" });
      } catch (e) {
        if (alive) setState({
          loading: false, data: null,
          error: e?.response?.status === 404 ? "Siden finnes ikke." : "Kunne ikke laste siden.",
        });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  useNeutralMeta();

  if (state.loading || state.data === null) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center">
        <div className="font-manrope text-[var(--cream-soft)] text-sm">Laster…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-2">Ikke tilgjengelig</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)]">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center px-6 py-10">
      <div className="max-w-sm w-full text-center" data-testid="link-landing">
        <button
          onClick={() => navigate(`/link/${slug}/gate`)}
          className="btn-magenta text-base px-10 py-3 w-full"
          data-testid="link-continue-btn"
        >
          Se mine linker
        </button>
      </div>
    </div>
  );
}

// ---------- SIDE 2: Age gate ----------

export function LinkGate() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useNeutralMeta();

  const confirm = useCallback(async () => {
    setSubmitting(true);
    setError("");
    try {
      await publicApi.post(`/link/${slug}/confirm-age`);
      navigate(`/link/${slug}/links`);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Siden finnes ikke." : "Kunne ikke fortsette. Prøv igjen.");
      setSubmitting(false);
    }
  }, [slug, navigate]);

  return (
    <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center px-6 py-10">
      <div className="max-w-sm w-full text-center space-y-6" data-testid="link-gate">
        <h1 className="font-fraunces italic text-2xl text-[var(--cream)]">
          Bekreft alder
        </h1>
        <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed">
          Innhold på denne siden er kun for personer over 18 år.
          Ved å fortsette bekrefter du at du er 18 år eller eldre.
        </p>

        {error && (
          <div className="border-l-2 border-[var(--magenta)] pl-3 py-2 text-sm text-[var(--cream)] text-left">
            {error}
          </div>
        )}

        <div className="space-y-3 pt-2">
          <button
            onClick={confirm}
            disabled={submitting}
            className="btn-magenta text-base px-10 py-3 w-full disabled:opacity-50"
            data-testid="link-age-confirm-btn"
          >
            {submitting ? "Fortsetter…" : "Jeg er 18 år eller eldre"}
          </button>
          <RouterLink
            to="/"
            className="block font-manrope text-xs text-[var(--cream-soft)] hover:text-[var(--cream)] underline underline-offset-4"
            data-testid="link-age-decline"
          >
            Jeg er under 18 år — ta meg vekk
          </RouterLink>
        </div>
      </div>
    </div>
  );
}

// ---------- SIDE 3: Faktisk link-liste ----------

export function LinkList() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await publicApi.get(`/link/${slug}/links`);
        if (!alive) return;
        if (r.data.gated) {
          navigate(`/link/${slug}/gate`, { replace: true });
          return;
        }
        setState({ loading: false, data: r.data, error: "" });
      } catch (e) {
        if (alive) setState({
          loading: false, data: null,
          error: e?.response?.status === 404 ? "Siden finnes ikke." : "Kunne ikke laste linker.",
        });
      }
    })();
    return () => { alive = false; };
  }, [slug, navigate]);

  useNeutralMeta(state.data?.display_name);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center">
        <div className="font-manrope text-[var(--cream-soft)] text-sm">Laster…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-2">Ikke tilgjengelig</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)]">{state.error}</p>
        </div>
      </div>
    );
  }

  const { display_name, bio, avatar_url, links } = state.data;
  const clickHref = (lid) => `${LINK_API}/link/${encodeURIComponent(slug)}/click/${encodeURIComponent(lid)}`;

  return (
    <div className="min-h-screen bg-[var(--noir)] py-10 px-6">
      <div className="max-w-sm mx-auto space-y-6" data-testid="link-list-page">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <ImgAvatar src={avatar_url} name={display_name} />
          </div>
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)]" data-testid="link-list-name">
            {display_name}
          </h1>
          {bio && (
            <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed">{bio}</p>
          )}
        </div>

        <div className="space-y-3 pt-4" data-testid="link-list">
          {links.length === 0 && (
            <div className="text-center font-manrope text-sm text-[var(--cream-soft)] italic py-6">
              Ingen aktive linker akkurat nå.
            </div>
          )}
          {links.map((l) => (
            <a
              key={l.id}
              href={clickHref(l.id)}
              rel="noopener nofollow"
              className="block border border-white/20 hover:border-[var(--magenta)] hover:bg-white/5 transition-colors px-5 py-4 group"
              data-testid={`link-item-${l.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-manrope text-base text-[var(--cream)] font-medium truncate">
                  {l.label}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {l.qr_image_path && (
                    <img
                      src={`${LINK_API}/media?path=${encodeURIComponent(l.qr_image_path)}`}
                      alt=""
                      className="w-12 h-12 border border-white/20"
                      data-testid={`link-qr-${l.id}`}
                    />
                  )}
                  <ExternalLink className="h-4 w-4 text-[var(--cream-soft)] group-hover:text-[var(--magenta)]" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
